import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import type { LabelConfigContextMethodType } from '../../../state/label-config-multi-assign.state';

import { LabelConfigEditorComponent } from './label-config-editor.component';
import { LabelConfigMultiAssignFacade } from './label-config-multi-assign.facade';
import { labelConfigMultiAssignProviders } from './label-config-editor-dialog.providers';

/** Bare close payload: `'submitted'` when rows were staged, `undefined` otherwise. */
export type LabelConfigDialogResult = 'submitted' | undefined;

/**
 * Everything the dialog needs to know, as plain values — the dialog never
 * learns about the opener's staging states.
 */
export interface LabelConfigEditorDialogData {
  /** The label being coloured; may be a temp uuid for a label staged this session. */
  labelUuid: string;
  /**
   * Method types the opener knows about that the backend query cannot return
   * (created or renamed in the current editing session). Empty is normal.
   */
  contextMethodTypes: LabelConfigContextMethodType[];
  /**
   * The method type the opener has in context, when it has one — its row opens
   * pre-checked and its colour for this label pre-loaded. Omitting the field is
   * how an opener with no method type in context says so.
   */
  contextMethodTypeId?: string;
}

/**
 * Dialog host for `LabelConfigEditorComponent` — one self-contained multi-assign
 * session per open.
 *
 * The component declares `labelConfigMultiAssignProviders` as its OWN providers:
 * every open builds a fresh state/effects/facade stack that Angular tears down
 * with the dialog. The SHARED `LabelConfigState` (the staged-config ledger the
 * submit writes into) and the save cascade are deliberately NOT in that set —
 * they resolve through the opener's injector chain, so pass
 * `injector: <opener's injector>` to `MatDialog.open`.
 *
 * Closes with the bare session outcome: a submit has already written its rows
 * into the shared ledger, so there is no payload left to hand back.
 */
@Component({
  selector: 'ts-label-config-editor-dialog',
  template: `
    <div class="label-config-editor-dialog__body">
      <ts-label-config-editor></ts-label-config-editor>
    </div>
  `,
  // The inner editor's host has no width of its own, so left alone it hugs its
  // content and the dialog looks cramped. Stretch it to the full dialog width
  // here, at the dialog seam.
  styles: [
    `
      .label-config-editor-dialog__body {
        padding: 16px;
      }

      ts-label-config-editor {
        display: flex;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatDialogModule, LabelConfigEditorComponent],
  providers: [...labelConfigMultiAssignProviders],
})
export class LabelConfigEditorDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional()
    public readonly dialogRef: MatDialogRef<LabelConfigEditorDialogComponent, LabelConfigDialogResult> | null,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    private readonly data: LabelConfigEditorDialogData | null,
    private readonly facade: LabelConfigMultiAssignFacade
  ) {}

  ngOnInit(): void {
    if (this.data) {
      this.facade.initialize({
        labelUuid: this.data.labelUuid,
        contextMethodTypes: this.data.contextMethodTypes ?? [],
        contextMethodTypeId: this.data.contextMethodTypeId,
      });
    }

    this.facade.outcome$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((outcome) => this.dialogRef?.close(outcome === 'submitted' ? 'submitted' : undefined));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
